'use client';

import * as React from 'react';
import classNames from 'classnames';
import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/react';
import { Portal } from '@/components/ui/Portal';
import LoadingContent from '../LoadingContent';
import Icon from '../Icon';
import './styles.scss';

export interface IControlCustomProps {
  /** Indicate value */
  hasValue: boolean;
  /** Inner fields */
  children: React.ReactNode;
  /** placeholder */
  placeholder: string;
  /** Disabled status */
  disabled?: boolean;
  /** Focus handler */
  onFocus?: React.DOMAttributes<HTMLDivElement>['onFocus'];
  /** Blur handler */
  onBlur?: React.DOMAttributes<HTMLDivElement>['onBlur'];
  /** Keyup handler */
  onClick?: React.InputHTMLAttributes<HTMLDivElement>['onClick'];
  /** Color higlight for errors */
  hasError?: boolean;
  /** className to append */
  className?: string;
  /** Loading */
  loading?: boolean;
  /** Layout to use */
  layout?: 'default' | 'mobile';
}

/**
 * Custom field control
 */
const ControlCustom: React.FunctionComponent<IControlCustomProps> = (props) => {
  const [hasFocus, setHasFocus] = React.useState(false);
  const [dropOpen, setDropOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const [popperElement, setPopperElement] = React.useState<HTMLDivElement | null>(null);

  const { floatingStyles } = useFloating({
    elements: {
      reference: wrapperRef.current,
      floating: popperElement,
    },
    open: dropOpen,
    placement: 'bottom-start',
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (dropOpen) {
        if (
          target &&
          wrapperRef.current &&
          !wrapperRef.current.contains(target as Node) &&
          popperElement &&
          !popperElement.contains(target as Node)
        ) {
          setDropOpen(false);
        }
      }
    };
    globalThis.addEventListener('click', handleClickOutside);
    return () => {
      globalThis.removeEventListener('click', handleClickOutside);
    };
  }, [dropOpen, popperElement, wrapperRef]);

  if (props.layout === 'mobile') {
    return (
      <div className={'control-custom-mobile'}>
        <div className={'control-custom-mobile__placeholder'}>{props.placeholder}</div>
        <div className={'control-custom__content'}>{props.children}</div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div
        className={classNames(
          'control-custom',
          'size-default',
          {
            ['has-error']: props.hasError,
            ['disabled']: props.disabled,
            ['focus']: hasFocus,
          },
          props.className,
        )}
        onClick={(e) => {
          e.preventDefault();
          if (wrapperRef.current && !hasFocus) {
            wrapperRef.current.focus();
          }
        }}
        onFocus={(e) => {
          setHasFocus(true);
          if (typeof props.onFocus === 'function') {
            props.onFocus(e);
          }
        }}
        onBlur={(e) => {
          setHasFocus(false);
          if (typeof props.onBlur === 'function') {
            props.onBlur(e);
          }
        }}
        role="button"
        tabIndex={0}
        ref={wrapperRef}
      >
        <div
          className={'control-custom__holder'}
          onClick={() => {
            setDropOpen(!dropOpen);
          }}
        >
          <div className={'control-custom__placeholder'}>{props.placeholder}</div>
          {props.loading && (
            <span className={'control-custom__loader'}>
              <LoadingContent size="tiny" />
            </span>
          )}
          {props.hasValue && (
            <div className={'control-custom__count'}>
              <Icon
                width={20}
                height={20}
                kind="check"
              />
            </div>
          )}
          <Icon
            width={16}
            height={16}
            kind={dropOpen ? 'arrow01-up' : 'arrow01-down'}
            wrapperClassName={'control-custom__arrow'}
          />
        </div>
      </div>
      <Portal>
        <div
          className={classNames('control-custom__drop', {
            ['is-open']: dropOpen,
          })}
          ref={setPopperElement}
          style={floatingStyles}
        >
          <div className={'control-custom__drop__inner'}>
            <div className={classNames('control-custom__content', 'control-custom__drop__list')}>{props.children}</div>
          </div>
        </div>
      </Portal>
    </React.Fragment>
  );
};

ControlCustom.displayName = 'ControlCustom';

export default ControlCustom;
